import { ro } from './ro';
import { en } from './en';
import { it } from './it';
import { fr } from './fr';
import { es } from './es';
import type { MessageKey, Messages } from './messages';

export type Lang = 'ro' | 'en' | 'it' | 'fr' | 'es';
export const LANGS: readonly Lang[] = ['ro', 'en', 'it', 'fr', 'es'];

const DICTS: Record<Lang, Messages> = { ro, en, it, fr, es };
const STORAGE_KEY = 'webSynth.lang.v1';

function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (LANGS as readonly string[]).includes(saved)) return saved as Lang;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return (LANGS as readonly string[]).includes(nav) ? (nav as Lang) : 'en';
}

let current: Lang = detect();

export const getLang = (): Lang => current;

/** A language's own name (e.g. 'English', 'Italiano'), for the selector. */
export const langName = (lang: Lang): string => DICTS[lang]['lang.name'];

/** Translate a key, filling `{name}` placeholders. Falls back to Romanian, then the key. */
export function t(key: MessageKey | string, params?: Record<string, string | number>): string {
  const k = key as MessageKey;
  let s = DICTS[current][k] ?? DICTS.ro[k] ?? String(key);
  if (params) for (const [name, value] of Object.entries(params)) s = s.split(`{${name}}`).join(String(value));
  return s;
}

/**
 * Switching language persists the choice and reloads. Everything renders from
 * t() at load, so a reload re-renders the whole UI in the new language while all
 * other settings survive in localStorage — far simpler and safer than a live
 * re-translate of every dynamic control.
 */
export function setLang(lang: Lang): void {
  if (lang === current) return;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  location.reload();
}

/** Fill [data-i18n] text nodes and [data-i18n-title] / [data-i18n-aria-label] attributes. */
export function applyDom(root: ParentNode = document): void {
  document.documentElement.lang = current;
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const key = el.dataset['i18n'];
    if (key) el.textContent = t(key);
  }
  const attrPairs: Array<{ sel: string; ds: string; attr: string }> = [
    { sel: '[data-i18n-title]', ds: 'i18nTitle', attr: 'title' },
    { sel: '[data-i18n-aria-label]', ds: 'i18nAriaLabel', attr: 'aria-label' },
  ];
  for (const { sel, ds, attr } of attrPairs) {
    for (const el of root.querySelectorAll<HTMLElement>(sel)) {
      const key = el.dataset[ds];
      if (key) el.setAttribute(attr, t(key));
    }
  }
}

// Config-label helpers — keys derived from stable ids.
export const presetLabel = (id: string): string => t(`preset.${id}`);
export const synthLabel = (id: string): string => t(`synth.${id}`);
export const waveformName = (id: string): string => t(`waveform.${id}`);
export const effectLabel = (key: string): string => t(`effect.${key}`);
export const paramLabel = (key: string): string => t(`param.${key}`);
export const instrumentLabel = (id: string): string => t(`instrument.${id}`);
export const meterLabel = (id: string): string => t(`meter.${id}`);
